import { IChargeProps } from 'src/components/enterprise/features/OrderHistory/OrderReceipt/Charges';
import {
  IOrdersReceipt,
  OrderSpecialFeeType,
  OrderPaymentType,
  FeeType,
  FeeItemProperty,
} from 'src/types/OrderHistory';
import {
  currencyFormatter,
  transformPenniesToDollar,
} from 'src/utilities/currency-utils';
import { orderPaymentTypeIsReward } from 'src/common/Order/utils';
import { GRUBHUB_PLUS_LABEL } from 'src/constants/brands';
import { TaxExemptionTooltip } from './TaxExemptionTooltip';

interface ICharge {
  name: string;
  amount: number;
  explanation?: React.ComponentType;
  newAmount?: string;
}

export function mapOrderToChargesProps(order: IOrdersReceipt): IChargeProps[] {
  return createCharges(order).map(formatAmount);
}

function createCharges(order: IOrdersReceipt) {
  const charges = [
    createItemSubtotalCharge(order),
    createPromoCodeCharge(order),
    createFreeDeliveryCharge(order),
    createDeliveryFeeCharge(order),
    createServiceFeeCharge(order),
    ...createGenericFeeCharges(order),
    createSalesTaxCharge(order),
    createTaxExemptionCharge(order),
    createTipCharge(order),
    createOrderSubtotalCharge(order),
    createLOCCharge(order),
    createRewardsCharge(order),
  ];
  // Remove non-truthy charges,
  // each charge creator function can decide to not show the charge by returning a non-truthy value
  return charges.filter((charge) => !!charge);
}

function createItemSubtotalCharge(order: IOrdersReceipt): ICharge {
  const { item_subtotal } = order;
  return (
    item_subtotal && {
      name: 'Item subtotal',
      amount: item_subtotal,
    }
  );
}

function createOrderSubtotalCharge(order: IOrdersReceipt): ICharge {
  const { total_charge } = order;
  return (
    total_charge && {
      name: 'Order subtotal',
      amount: total_charge,
    }
  );
}

function createLOCCharge(order: IOrdersReceipt): ICharge {
  const { loc_total } = order;
  return (
    loc_total && {
      name: 'Line of credit',
      amount: loc_total * -1,
    }
  );
}

function getCampaignDeliveryFeeDisplayText(
  order: IOrdersReceipt
): string | undefined {
  return order?.savings_breakdown?.fee_savings?.[FeeType.DELIVERY]
    ?.display_text;
}

function createFreeDeliveryCharge(order: IOrdersReceipt): ICharge {
  const {
    order_payments: { payments },
  } = order;

  const campaignDeliveryFee = getCampaignDeliveryFeeDisplayText(order);
  const freeDeliveryPayment = getFreeDeliveryPayment(payments);

  if (campaignDeliveryFee || !freeDeliveryPayment) return null;

  return {
    name: `${getBrand()} Free delivery`,
    amount: 0,
  };

  function getBrand(): string {
    return (
      freeDeliveryPayment.metadata.subscription_cobrand || GRUBHUB_PLUS_LABEL
    );
  }
}

function createPromoCodeCharge(order: IOrdersReceipt): ICharge {
  const {
    order_payments: { payments },
  } = order;

  const payment = getPromoCodePayment();

  return (
    payment && {
      name: `Promo code`,
      amount: payment.amount * -1,
    }
  );

  function getPromoCodePayment() {
    return payments.find(
      (payment) =>
        payment.type === OrderPaymentType.PROMO_CODE &&
        !payment?.metadata?.subscription_code
    );
  }
}

function createDeliveryFeeCharge(order: IOrdersReceipt): ICharge {
  const campaignDeliveryFee = getCampaignDeliveryFeeDisplayText(order);

  if (campaignDeliveryFee) {
    return createCampaignDeliveryFeeCharge(order, campaignDeliveryFee);
  }

  const freeDeliveryPayment = getFreeDeliveryPayment(
    order.order_payments.payments
  );
  if (freeDeliveryPayment) return null;

  return createRegularDeliveryFeeCharge(order);
}

function createCampaignDeliveryFeeCharge(
  order: IOrdersReceipt,
  campaignDeliveryFee: string
): ICharge {
  const deliveryFee = getFeeItemProperty(
    order,
    OrderSpecialFeeType.DELIVERY,
    FeeItemProperty.NON_ADJUSTED_AMOUNT
  );

  return (
    deliveryFee && {
      name: 'Delivery fee',
      amount: deliveryFee,
      newAmount: campaignDeliveryFee,
    }
  );
}

function createRegularDeliveryFeeCharge(order: IOrdersReceipt): ICharge {
  const deliveryFee = getFeeItemProperty(
    order,
    OrderSpecialFeeType.DELIVERY,
    FeeItemProperty.CALCULATED_AMOUNT
  );

  return (
    deliveryFee && {
      name: 'Delivery fee',
      amount: deliveryFee,
    }
  );
}

function getFreeDeliveryPayment(payments) {
  return payments.find(
    (payment) =>
      payment.type === OrderPaymentType.PROMO_CODE &&
      payment?.metadata?.subscription_code &&
      payment?.metadata?.subscription_cobrand?.startsWith(GRUBHUB_PLUS_LABEL)
  );
}

function createServiceFeeCharge(order: IOrdersReceipt): ICharge {
  const { savings_breakdown } = order;
  const campaignServiceFee =
    savings_breakdown?.fee_savings?.[FeeType.SERVICE]?.display_text;
  const serviceFee = calculateServiceFee();

  return (
    serviceFee && {
      name: 'Service fee',
      amount: serviceFee,
      ...(campaignServiceFee && {
        newAmount: campaignServiceFee,
      }),
    }
  );

  function calculateServiceFee() {
    const property = campaignServiceFee
      ? FeeItemProperty.NON_ADJUSTED_AMOUNT
      : FeeItemProperty.CALCULATED_AMOUNT;

    return [
      OrderSpecialFeeType.SERVICE,
      OrderSpecialFeeType.SERVICE_TOLL,
      OrderSpecialFeeType.CHAIN_SERVICE_FEE,
    ]
      .map((type) => getFeeItemProperty(order, type, property))
      .reduce((total, currentAmount) => total + currentAmount, 0);
  }
}

function createGenericFeeCharges(order: IOrdersReceipt): ICharge[] {
  const feeItems = order.order_charges.order_fees?.fee_items || [];
  const feeItemsWithoutSpecialHandling = feeItems.filter((feeItem) => {
    const specialFeeType = OrderSpecialFeeType[feeItem.type];
    return !specialFeeType;
  });
  return feeItemsWithoutSpecialHandling.map((feeItem) => ({
    name: feeItem.name,
    amount: feeItem.calculated_amount,
  }));
}

function createTipCharge(order: IOrdersReceipt): ICharge {
  const tip = order.order_charges.tip;
  return (
    tip && {
      name: 'Tip',
      amount: tip,
    }
  );
}

function createSalesTaxCharge(order: IOrdersReceipt): ICharge {
  const {
    order_charges: { exempted_taxes },
    sales_tax,
  } = order;

  const amount = exempted_taxes ? exempted_taxes.total : sales_tax;

  return {
    name: 'Sales tax',
    amount,
  };
}

function createTaxExemptionCharge(order: IOrdersReceipt): ICharge {
  const {
    order_charges: { exempted_taxes },
  } = order;

  return (
    exempted_taxes && {
      name: 'Tax exemption',
      explanation: TaxExemptionTooltip,
      amount: exempted_taxes.total * -1,
    }
  );
}

function createRewardsCharge(order: IOrdersReceipt): ICharge {
  const rewards = calculateRewards();
  return (
    rewards && {
      name: 'Rewards',
      amount: rewards,
    }
  );

  function calculateRewards() {
    const {
      order_payments: { payments },
    } = order;

    const totalRewards = payments
      .filter((payment) => orderPaymentTypeIsReward(payment.type))
      .map((payment) => payment.amount)
      .reduce((total, amount) => total + amount, 0);

    return totalRewards * -1;
  }
}

function getFeeItemProperty(
  order: IOrdersReceipt,
  feeType: string,
  property: FeeItemProperty
): number {
  const {
    order_charges: { order_fees: { fee_items = [] } = {} },
  } = order;

  const feeItem = fee_items.find((item) => item.type === feeType);

  return feeItem?.[property] ?? 0;
}

function formatAmount(order): IChargeProps {
  return { ...order, amount: formatCurrency(order.amount) };
}

function formatCurrency(value: number) {
  return currencyFormatter(Number(transformPenniesToDollar(value)));
}
